import { useEffect, useMemo, useRef, useState } from "react";
import {
  generatePrompts,
  businessLabel,
  categorizePrompt,
  RATIONALE_TEXTS,
  RATIONALE_LABEL,
} from "../data/prompts";
import { generateReadyTexts } from "../data/templates";
import AccionDeHoy from "../components/AccionDeHoy";
import { FOTO_GUIA } from "../data/fotoGuia";
import {
  Copy,
  Check,
  MessageCircle,
  Sparkles,
  RotateCcw,
  ThumbsUp,
  ThumbsDown,
  Lightbulb,
  X,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const WHATSAPP_NUMBER = "527442080244";
const WHATSAPP_MSG = "Hola, vi NegoIA y quiero saber más del plan mensual";
const CONSULT_MSG = "Hola Lalo, vi NegoIA y me interesa una estrategia personalizada para mi negocio 🙌";

// Small signature appended to every copied prompt/text.
const COPY_SIGNATURE = "Estrategia generada con NegoIA.mx 🤖";
// CTA appended to the "Enviarme por WhatsApp" share message.
const SHARE_CTA = "Prueba NegoIA gratis en negoia.mx — tu asesor de marketing de bolsillo";
const withSignature = (text) => `${text}\n\n${COPY_SIGNATURE}`;

// Invisible "meta-prompt" prefixed on every copied LLM prompt (NOT shown in the card).
const META_PROMPT = `Contexto del experto: Eres un consultor de marketing con 20 años de experiencia en PyMEs mexicanas. Conoces la realidad de negocios locales como taquerías, pollerías, gimnasios y tiendas de barrio en México. Antes de ejecutar la siguiente tarea, analiza el contexto del negocio e identifica su obstáculo más urgente. Genera respuestas ejecutables esta misma semana con celular y sin presupuesto extra. No uses tecnicismos.`;

// Legacy fallback for browsers/contexts where navigator.clipboard.writeText fails
// (notably older iOS Safari / WebView). Returns true on success.
function legacyCopy(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.readOnly = false;
    ta.contentEditable = "true";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const range = document.createRange();
    range.selectNodeContents(ta);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
    ta.setSelectionRange(0, 999999);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

async function copyToClipboard(text) {
  try {
    if (
      navigator.clipboard &&
      window.isSecureContext &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy */
  }
  return legacyCopy(text);
}

// ────────────────────────────────────────────────────────────────────────────
// CARD: Ready-to-publish text (default mode for Taquería / Pollería)
// ────────────────────────────────────────────────────────────────────────────
function ReadyTextCard({ item, idx, businessType }) {
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef(null);
  const hasPlaceholders = /\[[^\]]+\]/.test(item.text);
  const fotoGuia = FOTO_GUIA[businessType];

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    const ok = await copyToClipboard(withSignature(item.text));
    if (ok) {
      setCopied(true);
      trackEvent("copio_prompt", { tipo: item.title });
      toast.success("¡Copiado! Pégalo directo en tus redes", { duration: 1500 });
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        setCopied(false);
        resetTimerRef.current = null;
      }, 1500);
    } else {
      toast.error("No se pudo copiar. Selecciona el texto manualmente.");
    }
  };

  return (
    <div
      className="bg-white border-2 border-slate-200 rounded-2xl p-5 flex flex-col gap-4 shadow-sm"
      data-testid={`ready-text-card-${idx}`}
    >
      {hasPlaceholders && (
        <p
          className="font-semibold leading-snug"
          style={{ color: "#F97316", fontSize: "12px" }}
          data-testid={`ready-text-placeholder-hint-${idx}`}
        >
          ✏️ Personaliza los textos en [ ] con los datos de tu negocio antes de publicar
        </p>
      )}
      <div className="flex items-start gap-2">
        <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-orange-100 text-orange-600 shrink-0 mt-0.5">
          <Sparkles className="w-4 h-4" strokeWidth={2.5} />
        </span>
        <h3 className="text-lg font-bold text-slate-900 leading-snug">
          {item.title}
        </h3>
      </div>

      <pre className="text-[0.9rem] text-slate-700 bg-slate-50 p-4 rounded-xl border border-slate-100 whitespace-pre-wrap break-words font-sans leading-relaxed">
        {item.text}
      </pre>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-700 bg-orange-50 px-3 py-1.5 rounded-full">
          📱 Listo para publicar
        </span>
        <button
          type="button"
          onClick={handleCopy}
          data-testid={`copy-ready-text-button-${idx}`}
          className={[
            "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all active:scale-95",
            copied
              ? "bg-teal-600 text-white"
              : "bg-slate-900 text-white active:bg-slate-800",
          ].join(" ")}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" strokeWidth={3} />
              Copiado
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" strokeWidth={2.5} />
              Copiar texto
            </>
          )}
        </button>
      </div>

      <p
        className="text-xs font-semibold leading-snug -mt-1"
        style={{ color: "#F97316" }}
        data-testid={`ready-text-cta-${idx}`}
      >
        → Pégalo directo en Instagram, Facebook o WhatsApp.
      </p>

      {fotoGuia && (
        <p
          className="text-xs text-slate-500 leading-snug -mt-2"
          data-testid={`ready-text-foto-guia-${idx}`}
        >
          {fotoGuia}
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CARD: ChatGPT/Claude prompt (existing LLM flow)
// ────────────────────────────────────────────────────────────────────────────
function PromptCard({ prompt, idx, onShowRationale }) {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const resetTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    const textToCopy = withSignature(`${META_PROMPT}\n\n${prompt.text}`);
    const ok = await copyToClipboard(textToCopy);
    if (ok) {
      setCopied(true);
      trackEvent("copio_prompt", { tipo: prompt.title });
      toast.success("¡Copiado! Pégalo en ChatGPT o Claude", { duration: 1500 });
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        setCopied(false);
        resetTimerRef.current = null;
      }, 1500);
    } else {
      toast.error("No se pudo copiar. Intenta seleccionar el texto manualmente.");
    }
  };

  const openInLLM = async (url) => {
    const textToCopy = withSignature(`${META_PROMPT}\n\n${prompt.text}`);
    await copyToClipboard(textToCopy);
    toast.success("Prompt copiado. Pégalo al abrir la pestaña.", { duration: 2000 });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="bg-white border-2 border-slate-200 rounded-2xl p-5 flex flex-col gap-4 shadow-sm"
      data-testid={`prompt-result-card-${idx}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-orange-100 text-orange-600 shrink-0 mt-0.5">
            <Sparkles className="w-4 h-4" strokeWidth={2.5} />
          </span>
          <h3 className="text-lg font-bold text-slate-900 leading-snug">
            {prompt.title}
          </h3>
          <button
            type="button"
            onClick={() => onShowRationale(categorizePrompt(prompt.title))}
            data-testid={`rationale-button-${idx}`}
            aria-label="Por qué funciona este prompt"
            className="flex items-center justify-center w-7 h-7 rounded-lg bg-amber-50 text-amber-600 shrink-0 mt-0.5 active:bg-amber-100 active:scale-95 transition-all"
          >
            <Lightbulb className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <pre className="text-[0.9rem] text-slate-700 bg-slate-50 p-4 rounded-xl border border-slate-100 whitespace-pre-wrap break-words font-sans leading-relaxed">
        {prompt.text}
      </pre>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 bg-teal-50 px-3 py-1.5 rounded-full">
          <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.5} />
          Úsalo en ChatGPT o Claude
        </span>
        <button
          type="button"
          onClick={handleCopy}
          data-testid={`copy-prompt-button-${idx}`}
          className={[
            "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all active:scale-95",
            copied
              ? "bg-teal-600 text-white"
              : "bg-slate-900 text-white active:bg-slate-800",
          ].join(" ")}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" strokeWidth={3} />
              Copiado
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" strokeWidth={2.5} />
              Copiar prompt
            </>
          )}
        </button>
      </div>

      <p
        className="text-xs font-semibold leading-snug -mt-1"
        style={{ color: "#F97316" }}
        data-testid={`copy-cta-${idx}`}
      >
        → Copia este texto y pégalo en ChatGPT o Claude ahora mismo.
      </p>

      {/* Open in ChatGPT / Claude */}
      <div className="flex items-center gap-2 flex-wrap" data-testid={`llm-open-row-${idx}`}>
        <button
          type="button"
          onClick={() => openInLLM("https://chat.openai.com/")}
          data-testid={`open-chatgpt-${idx}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white active:bg-emerald-700 active:scale-95 transition-all"
        >
          <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} />
          Abrir en ChatGPT
        </button>
        <button
          type="button"
          onClick={() => openInLLM("https://claude.ai/new")}
          data-testid={`open-claude-${idx}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-[#C26B3C] text-white active:bg-[#a85a30] active:scale-95 transition-all"
        >
          <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} />
          Abrir en Claude
        </button>
      </div>

      {/* Feedback row */}
      <div
        className="pt-3 border-t border-slate-100"
        data-testid={`feedback-row-${idx}`}
      >
        {feedback === null ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFeedback("up")}
              data-testid={`feedback-up-${idx}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 active:bg-teal-50 active:border-teal-300 active:text-teal-800 transition-all"
            >
              <ThumbsUp className="w-3.5 h-3.5" strokeWidth={2.4} />
              Me funcionó
            </button>
            <button
              type="button"
              onClick={() => setFeedback("down")}
              data-testid={`feedback-down-${idx}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 active:bg-orange-50 active:border-orange-300 active:text-orange-700 transition-all"
            >
              <ThumbsDown className="w-3.5 h-3.5" strokeWidth={2.4} />
              Necesita mejorar
            </button>
          </div>
        ) : (
          <p
            className="text-xs font-semibold text-teal-700"
            data-testid={`feedback-thanks-${idx}`}
          >
            ¡Gracias! Tu opinión mejora NegoIA.
          </p>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// SCREEN
// ────────────────────────────────────────────────────────────────────────────
export default function Screen3Results({ businessType, answers, city, ownerName, otherText, goal, onRestart }) {
  const prompts = useMemo(
    () => generatePrompts(businessType, answers, city, otherText, goal),
    [businessType, answers, city, otherText, goal],
  );

  const readyTexts = useMemo(
    () =>
      generateReadyTexts({
        businessType,
        answers,
        city,
        ownerName,
        otherText,
      }),
    [businessType, answers, city, ownerName, otherText],
  );

  const label = businessLabel(businessType);
  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`;
  const greetName = (ownerName && ownerName.trim()) || (city && city.trim());

  // Mode toggle: "ready" (default when available) or "llm".
  // When no ready templates exist for this (giro, answers, date), we force "llm".
  const hasReady = Array.isArray(readyTexts) && readyTexts.length > 0;
  const [mode, setMode] = useState(hasReady ? "ready" : "llm");

  // Derived "effective" mode — guarantees we never render ready-mode UI when
  // there are no ready templates, even if `mode` got out of sync (e.g. user
  // restarts the flow with a different giro). Replaces the previous effect-driven
  // sync that the lint flagged as react-hooks/set-state-in-effect.
  const effectiveMode = hasReady ? mode : "llm";

  const [rationaleCategory, setRationaleCategory] = useState(null);
  const closeRationale = () => setRationaleCategory(null);

  useEffect(() => {
    if (!rationaleCategory) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setRationaleCategory(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [rationaleCategory]);

  const shareText =
    (effectiveMode === "ready" && hasReady
      ? readyTexts.map((t) => `${t.title}\n\n${t.text}`).join("\n\n---\n\n")
      : prompts.map((p) => p.text).join("\n\n")) + `\n\n${SHARE_CTA}`;

  return (
    <div
      className="flex flex-col min-h-[100dvh] px-6 pt-8 pb-10 animate-fade-in-up"
      data-testid="screen-3"
    >
      {/* Header */}
      <div className="mb-1">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-teal-700 bg-teal-50 px-3 py-1 rounded-full">
          <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
          Listo
        </span>
      </div>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight mt-3">
        {effectiveMode === "ready" && hasReady ? (
          <>
            Tu contenido listo para tu{" "}
            <span className="text-orange-500">{label}</span>
          </>
        ) : (
          <>
            Tus 3 prompts para tu{" "}
            <span className="text-orange-500">{label}</span>
          </>
        )}
      </h1>
      <p className="text-base text-slate-500 mt-2 leading-relaxed">
        {effectiveMode === "ready" && hasReady
          ? "Copia cada texto y publícalo directo en tus redes."
          : "Copia cada prompt, pégalo en ChatGPT o Claude y obtén tu estrategia en segundos."}
      </p>

      {/* Personalized welcome message */}
      <p
        className="text-2xl sm:text-3xl font-extrabold leading-tight mt-6"
        style={{ color: "#F97316" }}
        data-testid="welcome-message"
      >
        {greetName ? `¡Hola ${greetName}! 👋 ` : "¡Hola! 👋 "}
        Aquí están tus estrategias para {label}.
      </p>

      {/* Mode toggle — only shown when a ready template is available */}
      {hasReady && (
        <div
          className="mt-6 inline-flex p-1 bg-slate-100 rounded-xl w-full"
          data-testid="mode-toggle"
          role="tablist"
          aria-label="Modo de salida"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === "ready"}
            onClick={() => setMode("ready")}
            data-testid="mode-toggle-ready"
            className={[
              "flex-1 px-3 py-2.5 rounded-lg text-xs sm:text-sm font-bold transition-all",
              mode === "ready"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 active:text-slate-700",
            ].join(" ")}
          >
            📱 Texto listo para publicar
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "llm"}
            onClick={() => setMode("llm")}
            data-testid="mode-toggle-llm"
            className={[
              "flex-1 px-3 py-2.5 rounded-lg text-xs sm:text-sm font-bold transition-all",
              mode === "llm"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 active:text-slate-700",
            ].join(" ")}
          >
            🤖 Usar con ChatGPT o Claude
          </button>
        </div>
      )}

      {/* Cards */}
      <div className="flex flex-col gap-4 mt-6">
        {effectiveMode === "ready" && hasReady
          ? readyTexts.map((t, i) => (
              <ReadyTextCard key={`ready-${i}`} item={t} idx={i} businessType={businessType} />
            ))
          : prompts.map((p, i) => (
              <PromptCard
                key={`llm-${i}`}
                prompt={p}
                idx={i}
                onShowRationale={setRationaleCategory}
              />
            ))}
      </div>

      {/* Rationale modal */}
      {rationaleCategory && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={closeRationale}
          data-testid="rationale-modal-backdrop"
        >
          <div
            className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
            data-testid="rationale-modal"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={closeRationale}
              data-testid="rationale-modal-close"
              aria-label="Cerrar"
              className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center rounded-full text-slate-500 active:bg-slate-100 active:text-slate-800 transition-all"
            >
              <X className="w-5 h-5" strokeWidth={2.4} />
            </button>
            <div className="flex items-center gap-2 mb-3 pr-8">
              <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-amber-50 text-amber-600">
                <Lightbulb className="w-5 h-5" strokeWidth={2.4} />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-amber-600">
                  Por qué funciona
                </p>
                <p className="text-base font-bold text-slate-900 leading-tight">
                  {RATIONALE_LABEL[rationaleCategory]}
                </p>
              </div>
            </div>
            <p
              className="text-sm text-slate-700 leading-relaxed"
              data-testid="rationale-modal-text"
            >
              {RATIONALE_TEXTS[rationaleCategory]}
            </p>
          </div>
        </div>
      )}

      {/* Iteration tip — only relevant for LLM mode */}
      {effectiveMode === "llm" && (
        <p
          className="mt-6 text-slate-500 leading-relaxed"
          style={{ fontSize: "12px" }}
          data-testid="iteration-tip"
        >
          💡 Tip: Copia el prompt completo. Si quieres personalizar, agrega detalles al final. Modificar el texto original puede reducir la calidad del resultado.
        </p>
      )}

      {/* WhatsApp share button */}
      <a
        href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("wa_share_results", { mode, businessType })}
        data-testid="whatsapp-share-button"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 text-white font-bold text-base py-4 rounded-2xl shadow-lg active:scale-[0.98] transition-all"
        style={{ backgroundColor: "#25D366", boxShadow: "0 10px 20px -6px rgba(37, 211, 102, 0.45)" }}
      >
        📲 {effectiveMode === "ready" && hasReady
          ? "Enviarme los textos por WhatsApp"
          : "Enviarme los prompts por WhatsApp"}
      </a>
      <p
        className="mt-3 text-center text-xs text-slate-500 leading-snug"
        data-testid="whatsapp-share-helper"
      >
        💬 Ya están en tu WhatsApp. Ábrelo y copia el que necesites.
      </p>

      {/* Consultancy CTA — dark personalised-strategy block */}
      <div
        className="mt-8 rounded-2xl p-7 sm:p-8 text-center"
        style={{ backgroundColor: "#1a1a1a" }}
        data-testid="consult-cta-block"
      >
        <h3
          className="text-2xl sm:text-3xl font-extrabold text-white leading-tight"
          data-testid="consult-cta-title"
        >
          ¿Quieres resultados más rápido?
        </h3>
        <p
          className="mt-3 leading-relaxed"
          style={{ color: "#d1d5db", fontSize: "13px" }}
          data-testid="consult-cta-subtitle"
        >
          Un negocio en Acapulco generó clientes nuevos en su primera semana.
          Podemos hacer lo mismo contigo — de forma personalizada.
        </p>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(CONSULT_MSG)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent("wa_consult_screen3", { businessType });
            trackEvent("clic_conversion", { source: "wa_consult_screen3", businessType });
          }}
          data-testid="consult-cta-button"
          className="mt-6 w-full inline-flex items-center justify-center text-white font-bold rounded-2xl py-4 active:scale-[0.98] transition-all"
          style={{ backgroundColor: "#F97316", fontSize: "15px", boxShadow: "0 10px 20px -6px rgba(249, 115, 22, 0.45)" }}
        >
          Quiero una estrategia personalizada →
        </a>
        <p
          className="mt-4 text-center leading-snug"
          style={{ color: "#9ca3af", fontSize: "11px" }}
          data-testid="consult-cta-trust"
        >
          📲 Respondo en menos de 2 horas · Primera consulta sin costo
        </p>
      </div>

      {/* Restart link */}
      <AccionDeHoy businessType={businessType} reto={answers?.reto} />

      <button
        type="button"
        onClick={onRestart}
        data-testid="restart-button"
        className="mt-6 inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 active:text-slate-700 py-2"
      >
        <RotateCcw className="w-4 h-4" strokeWidth={2.2} />
        Generar de nuevo
      </button>

      {/* Divider */}
      <div className="my-10 border-t-2 border-dashed border-slate-200" />

      {/* Upsell */}
      <div className="text-center">
        <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 leading-tight">
          ¿Quieres <span className="text-orange-500">estrategias nuevas</span> cada semana para tu negocio?
        </h2>

        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent("wa_upsell_mensual", { businessType });
            trackEvent("clic_conversion", { source: "plan_49_mensual", businessType });
          }}
          data-testid="whatsapp-upsell-button"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-bold text-lg py-4 rounded-2xl shadow-lg shadow-orange-500/30 active:bg-orange-600 active:scale-[0.98] transition-all"
        >
          <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
          Ver plan desde $49/mes
        </a>
        <p className="text-xs text-slate-400 mt-3">
          Sin compromisos. Te explicamos en 2 minutos.
        </p>
      </div>
    </div>
  );
}
